import type { ReactNode } from "react";
import type { SidebarSpace } from "@/utils/sidebar";

export type SearchSpace = Extract<SidebarSpace, "myspace" | "company">;

export interface ISearchEntry {
  /** Unique key — the destination path. */
  path: string;
  /** Primary label shown in the result row. */
  label: string;
  /** Owning module's display name (shown in breadcrumb). */
  module: string;
  /** Third breadcrumb level — hTab section label for 3-level deep pages. */
  parentLabel?: string;
  /** True for a module landing page, false for a deep-link sub-page. */
  isModule: boolean;
  space: SearchSpace;
  icon: ReactNode;
  /** Synonyms fed to cmdk's fuzzy filter. */
  keywords: string[];
}

/**
 * A selectable scope "tag" shown above the results as a chip. Single-select:
 * exactly one is active at a time (default "all"). Ids are namespaced so the
 * predicate can tell space filters from module filters:
 *   "all" · "space:myspace" · "space:company" · `module:${label}`
 */
export type SearchFilterId = "all" | `space:${SearchSpace}` | `module:${string}`;

export type SearchFilterKind = "all" | "space" | "module";

export interface SearchFilter {
  id: SearchFilterId;
  label: string;
  kind: SearchFilterKind;
  /** Owning module's icon — shown only on module chips. */
  icon?: ReactNode;
}

/** True when `entry` belongs to the given filter scope. */
export const matchesFilter = (entry: ISearchEntry, filterId: SearchFilterId): boolean => {
  if (filterId === "all") return true;
  if (filterId.startsWith("space:")) return entry.space === filterId.slice(6);
  if (filterId.startsWith("module:")) return entry.module === filterId.slice(7);
  return true;
};
